<template>
  <Teleport to="body">
    <div class="howto-backdrop" @click.self="close">
      <div class="howto" role="dialog" aria-label="How to play">
        <button class="howto__close" aria-label="Close" @click="close">✕</button>

        <div class="howto__body">
          <h3>{{ slide.title }}</h3>

          <div v-if="slide.dice" class="howto__dice" aria-hidden="true">
            <DieFace v-for="(die, i) in slide.dice" :key="i" :value="die" />
          </div>

          <!-- eslint-disable-next-line vue/no-v-html -- content is this file's own
               constant strings below, never user input -->
          <p v-for="(line, i) in slide.lines" :key="i" class="howto__line" v-html="line" />
        </div>

        <div class="howto__nav">
          <button class="secondary" :disabled="index === 0" @click="index -= 1">Back</button>
          <span class="howto__dots">
            <i
              v-for="(s, i) in slides"
              :key="i"
              class="howto__dot"
              :class="{ 'howto__dot--on': i === index }"
            />
          </span>
          <button v-if="index < slides.length - 1" class="primary howto__next" @click="index += 1">
            Next
          </button>
          <button v-else class="primary howto__next" @click="close">
            Got it
          </button>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script>
import DieFace from '@/components/game/DieFace.vue';

// The walkthrough. Matt: friends he invites have often never played, and they
// should be able to "get a sense of the lay of the land when they first
// arrive" without waiting for him to teach it. Same pattern as the sibling
// games' HowToPlay - slides, three ways out.
//
// The pairing slide's worked example (3-4-2-6 pairing three ways) is the
// rulebook's own (rules.pdf p1, Rolling) and is asserted against the engine's
// pairingsOf in the component test, so the tutorial cannot drift from the
// rules it teaches.
export const EXAMPLE_DICE = [3, 4, 2, 6];
export const EXAMPLE_SUMS = ['7 & 8', '5 & 10', '6 & 9'];

const SLIDES = [
  {
    title: 'Claim three columns',
    lines: [
      'The board is eleven columns, one for every number two dice can make. The middle columns are long; the 2 and the 12 are short — because you\'ll roll a 7 far more often than snake eyes.',
      'Climb a column to the top and claim it. <strong>First player to claim three columns wins.</strong>',
      'Your turn is a series of rolls, and after every one of them you\'ll face the only question in the game: stop, or roll again?',
    ],
  },
  {
    title: 'Roll four dice, pair them up',
    dice: EXAMPLE_DICE,
    lines: [
      'Roll all four dice, then split them into <strong>two pairs, any way you like</strong>. This roll can be 7 &amp; 8, or 5 &amp; 10, or 6 &amp; 9 — your choice.',
      'Each pair\'s total moves a <strong>white runner</strong> one step up that column.',
      'You only have <strong>three runners</strong> a turn. Once all three are on the board, every roll has to move one of <em>them</em> — that\'s where the trouble starts.',
    ],
  },
  {
    title: 'Stop… or roll again?',
    lines: [
      'After each roll you can <strong>stop</strong>: your runners\' progress is banked as your own markers, safe for the rest of the game. Next turn you\'ll carry on climbing from there.',
      'Or <strong>roll again</strong> and push higher. But if a roll comes up where <strong>no pair can move any runner — you bust</strong>: everything the runners gained this turn is gone, and the dice pass on.',
      'That\'s the whole game. It\'s called Can\'t Stop because you won\'t.',
    ],
  },
  {
    title: 'Claiming a column',
    lines: [
      'Get a runner to the <strong>top of a column and stop</strong> — the column is yours, permanently. Everyone else\'s markers there are swept off.',
      'A claimed column is <strong>dead for everybody</strong>, you included: no pair can ever use that number again. The board shrinks as the game goes on, and busting gets easier.',
      'Reaching the top isn\'t enough — you have to <strong>stop</strong> to bank the claim. Summit three columns and the game is yours. Good luck.',
    ],
  },
];

export default {
  name: 'HowToPlay',
  components: { DieFace },
  emits: ['close'],
  data() {
    return { index: 0, slides: SLIDES };
  },
  computed: {
    slide() { return this.slides[this.index]; },
  },
  methods: {
    close() { this.$emit('close'); },
  },
};
</script>
